import { create } from "zustand"

import type {
  IPeopleFilters,
  IPersonOS,
  PeopleScope,
  PeopleSource
} from "~/types/people-os"

// A "sub-tab" inside the People list scope. The Connected sub-tab adds the
// Disposition + Warmth columns (Disposition is dialer-blocked, see constants).
export type PeopleListSubTab = "all" | "connected"

// Source-filter tab value: "all" plus the five real sources.
export type SourceFilter = "all" | PeopleSource

export type PeopleSortDir = "asc" | "desc"

export type CreateListIntent = "add" | "move"

interface IPeopleOsStore {
  // Top scope toggle: Recommended triage vs People list.
  scope: PeopleScope
  setScope: (scope: PeopleScope) => void

  // Sub-tab within People list (all / connected). Unused by the tab right now —
  // the Connected sub-tab is parked until the dialer work lands (ENG-2480 item 5).
  subTab: PeopleListSubTab
  setSubTab: (subTab: PeopleListSubTab) => void

  // The list open in the People-list scope. Store-level (not local to the tab)
  // so the import flow can drop the user onto the list it just filled.
  activeListId: number | null
  setActiveListId: (id: number | null) => void

  // Source-filter tabs (CRM / Recommended / Exec transition / Speaker / Search).
  sourceFilter: SourceFilter
  setSourceFilter: (source: SourceFilter) => void

  // Free-text search over the current grid.
  query: string
  setQuery: (query: string) => void

  // Sort + filter. (Saved views are gone — ENG-2480 item 4 dropped the
  // "All people" dropdown that was the only reader of that state.)
  sortKey: string
  sortDir: PeopleSortDir
  setSort: (key: string, dir: PeopleSortDir) => void
  filters: IPeopleFilters
  setFilters: (filters: IPeopleFilters) => void

  // Bulk selection (row keys = person ids). hasTouchedSelection distinguishes
  // "nobody has ticked a row yet" (dialQueue defaults to the whole list) from
  // "the rep ticked rows and then unticked all of them" (selectedIds is also
  // [] here, but it means dial nobody, not dial everybody) — both look
  // identical as a bare empty array without this flag. Set by setSelectedIds
  // (an actual row-selection action); cleared by clearSelection/setScope,
  // which are "start fresh" resets, not a selection choice.
  selectedIds: string[]
  hasTouchedSelection: boolean
  setSelectedIds: (ids: string[]) => void
  clearSelection: () => void

  // Person 360 drawer (Overview only) — the person id being viewed.
  activePersonId: string | null
  setActivePersonId: (id: string | null) => void

  // Pre-meeting brief drawer — the full person being briefed (the platform
  // brief drawer needs linkedin_url + name + account context, not just an id).
  briefPerson: IPersonOS | null
  setBriefPerson: (person: IPersonOS | null) => void

  // Who the snooze picker is open for. In the store rather than the grid
  // because the in-call panel opens the same picker, and it renders inside an
  // expanded row several levels below the component that owns the modal.
  // Only what the picker needs, so the in-call panel can open it with a
  // RunPerson - the queue's trimmed shape, not a full IPersonOS.
  snoozeFor: { id: string; name: string } | null
  setSnoozeFor: (person: { id: string; name: string } | null) => void

  // Supporting surfaces.
  searchDrawerOpen: boolean
  setSearchDrawerOpen: (open: boolean) => void
  importModalOpen: boolean
  setImportModalOpen: (open: boolean) => void

  // The CSV import currently running. Lives here rather than inside the modal so
  // "Run in background" (or any close) keeps the status poll alive — the
  // root-mounted PeopleImportWatcher observes it and re-opens the success card
  // when the batches land.
  importWorkflowId: string | null
  setImportWorkflowId: (id: string | null) => void

  // Create-list modal: which person ids are being added (from selection or a
  // single row). Open when the array is non-empty. `createListPeople` carries the
  // full person objects so not-yet-added candidates can be promoted server-side.
  createListIds: string[]
  createListPeople: IPersonOS[]
  // "add" files the ids into a list; "move" also takes them off
  // `createListFromListId`, which is excluded from the target choices.
  createListIntent: CreateListIntent
  createListFromListId: number | null
  openCreateList: (
    ids: string[],
    people?: IPersonOS[],
    opts?: { intent?: CreateListIntent; fromListId?: number | null }
  ) => void
  closeCreateList: () => void

  // ICP settings drawer (Settings -> Ideal Customer Profile).
  icpDrawerOpen: boolean
  setIcpDrawerOpen: (open: boolean) => void

  // Dialer history modal — the rep's full call log, all people, both tabs.
  dialerHistoryOpen: boolean
  setDialerHistoryOpen: (open: boolean) => void

  // Whether the People section is in dialer mode.
  //
  // In the store rather than in PeopleOsTab because two things above the tab
  // need it. The tab STRIP owns the scope tabs and the call/history row, which
  // both have to disappear in dialer mode, and it sits a level up from the
  // component that held this as local state. And a session has to survive
  // navigating away, which local state cannot do - it unmounts with the route.
  dialerMode: boolean
  setDialerMode: (on: boolean) => void
}

const usePeopleOsStore = create<IPeopleOsStore>((set) => ({
  // Land on the seller's own People list, not the Recommended feed. With no
  // list selected this opens the all-lists index, so the first thing the tab
  // shows is "here are your lists".
  scope: "list",
  setScope: (scope) =>
    set({ scope, selectedIds: [], hasTouchedSelection: false }),

  subTab: "all",
  setSubTab: (subTab) => set({ subTab }),

  activeListId: null,
  // Changing which list is open clears the selection, exactly as changing
  // scope does. Without this, ticking rows in one list and then opening
  // another left the first list's ids selected and let the next ticks pile on
  // top - a selection spanning two lists, which every bulk action downstream
  // (add to list, dial, enrich) then treated as one set (ENG-3382).
  setActiveListId: (activeListId) =>
    set({ activeListId, selectedIds: [], hasTouchedSelection: false }),

  sourceFilter: "all",
  setSourceFilter: (sourceFilter) => set({ sourceFilter }),

  query: "",
  setQuery: (query) => set({ query }),

  sortKey: "warmth",
  sortDir: "desc",
  setSort: (sortKey, sortDir) => set({ sortKey, sortDir }),
  filters: {},
  setFilters: (filters) => set({ filters }),

  selectedIds: [],
  hasTouchedSelection: false,
  setSelectedIds: (selectedIds) =>
    set({ selectedIds, hasTouchedSelection: true }),
  clearSelection: () => set({ selectedIds: [], hasTouchedSelection: false }),

  activePersonId: null,
  setActivePersonId: (activePersonId) => set({ activePersonId }),

  briefPerson: null,
  setBriefPerson: (briefPerson) => set({ briefPerson }),

  snoozeFor: null,
  setSnoozeFor: (snoozeFor) => set({ snoozeFor }),

  searchDrawerOpen: false,
  setSearchDrawerOpen: (searchDrawerOpen) => set({ searchDrawerOpen }),
  importModalOpen: false,
  setImportModalOpen: (importModalOpen) => set({ importModalOpen }),

  importWorkflowId: null,
  setImportWorkflowId: (importWorkflowId) => set({ importWorkflowId }),

  createListIds: [],
  createListPeople: [],
  createListIntent: "add",
  createListFromListId: null,
  openCreateList: (createListIds, people = [], opts = {}) =>
    set({
      createListIds,
      createListPeople: people,
      createListIntent: opts.intent ?? "add",
      createListFromListId: opts.fromListId ?? null
    }),
  closeCreateList: () =>
    set({
      createListIds: [],
      createListPeople: [],
      createListIntent: "add",
      createListFromListId: null
    }),

  icpDrawerOpen: false,
  setIcpDrawerOpen: (icpDrawerOpen) => set({ icpDrawerOpen }),

  dialerHistoryOpen: false,
  setDialerHistoryOpen: (dialerHistoryOpen) => set({ dialerHistoryOpen }),

  dialerMode: false,
  setDialerMode: (dialerMode) => set({ dialerMode })
}))

export default usePeopleOsStore
